/**
 * @file        src/pages/flight/ConvenienceFee.jsx
 * @description Convenience Fee (CRUD) over convenience_fees.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (B2C Flight Management).
 *   - 2026-08-03 Claude — Required Brand field (registry code, no default) —
 *                          the API now demands `brand` on create.
 *   - 2026-08-10 Claude — Added the `applicable_role` field (All/B2C/B2B — the
 *                          column existed since migration 026 but no admin
 *                          screen exposed it) and title/subtitle/audience props
 *                          so CorporateConvenienceFee.jsx can reuse this page
 *                          fixed to audience=b2b instead of forking it.
 *   - 2026-09-12 Claude — Spelled out the "newest active rule wins" rule in a
 *                          notice bar (several rows can sit in this list and only
 *                          one is ever charged), and STOPPED showing the "Applies
 *                          To" dropdown: the screen you are on already decides it
 *                          (this page b2c, CorporateConvenienceFee.jsx b2b), so
 *                          the choice only let an admin file a corporate fee from
 *                          the retail screen and then not find it. The column
 *                          stays in the table so an inherited 'All' row (created
 *                          before this, or by an older client) is still visible.
 *   - 2026-09-15 Claude — Note now also states WHAT the fee is charged on and
 *                          WHERE it lands in the order of operations: on
 *                          Fare + Taxes + Markup + Add-ons, before the discount,
 *                          and inside the discountable total — so on flights (but
 *                          not hotels) a discount or coupon also comes off the fee.
 *   - 2026-10-05 Claude — Airline Type / Airline Code / Fare Type, as on Flight
 *                          Markup (migration 074). With scoped rules "newest wins"
 *                          no longer describes the pick: the notice now says the
 *                          most specific matching rule applies.
 *   - 2026-10-06 Claude — Domestic / International (migration 076): a trip with
 *                          any leg outside India is international. Shared by the
 *                          Corporate screen through this component.
 */
import SimpleLookupPage from '../package/SimpleLookupPage';
import { FLIGHT_FEE_BASE_NOTE } from '../../lib/pricingNotes';
import { flightScopeFields, flightScopeError } from '../../lib/fareTypes';
import { domIntField } from '../../lib/domInt';

const TYPE = [{ value: 'flat', label: 'Flat' }, { value: 'percentage', label: 'Percentage' }];
// Label map for the read-only "Applies To" column (the field is `form: false` —
// the SCREEN decides the audience, it is never picked in the modal). 'all' is
// retired (admin migration 055) and survives here only so a row on a tenant DB
// that has not been migrated yet reads as the legacy wildcard instead of raw text.
const ROLE = [
  { value: 'b2c', label: 'B2C (retail)' },
  { value: 'b2b', label: 'B2B / Corporate' },
  { value: 'all', label: 'All (legacy)' },
];

export default function ConvenienceFee({
  title = 'Convenience Fee',
  subtitle = 'B2C Flight · convenience fee rules',
  audience = 'b2c',
} = {}) {
  return (
    <SimpleLookupPage
      title={title}
      subtitle={subtitle}
      endpoint="/convenience-fees"
      queryRoot={`convenience-fees-${audience}`}
      addLabel="Add Convenience Fee"
      entityLabel="Convenience fee"
      listParams={{ audience }}
      notice={<span>Only <b>one</b> convenience fee is ever charged: of the active rules for the booking&apos;s brand and buyer type that match the fare, the <b>most specific</b> wins (Airline&nbsp;Wise beats ALL, a Fare&nbsp;Type-specific rule beats one with no fare type, and a Domestic- or International-only rule beats one for both); between equally specific rules the <b>most recently added</b> wins. If that rule&apos;s Min/Max range does not cover the fare, <b>no</b> fee is charged — it does not fall back to another rule.{FLIGHT_FEE_BASE_NOTE}</span>}
      fields={[
        { key: 'brand', label: 'Brand', type: 'brand', required: true },
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'fee_type', label: 'Fee Type', type: 'select', required: true, options: TYPE },
        { key: 'fee_value', label: 'Fee Value', type: 'number', required: true, min: 0, step: '0.01' },
        { key: 'min_amount', label: 'Min Amount', type: 'number', min: 0, step: '0.01' },
        { key: 'max_amount', label: 'Max Amount', type: 'number', min: 0, step: '0.01' },
        // Which airline / fare family the fee applies to (blank = every one).
        ...flightScopeFields(),
        // Domestic / International trips (blank = both).
        domIntField(),
        // Fixed by the screen, not chosen: no input in the modal (`form: false`),
        // but still a table column so an older 'All' row reads as what it is.
        { key: 'applicable_role', label: 'Applies To', type: 'select', options: ROLE, form: false, default: audience === 'b2b' ? 'b2b' : 'b2c' },
      ]}
      validate={(form) => {
        // Max Amount cannot be less than Min Amount (only when both are given).
        const errs = { ...flightScopeError(form) };
        const min = form.min_amount, max = form.max_amount;
        if (min !== '' && min != null && max !== '' && max != null && Number(min) > Number(max)) {
          errs.max_amount = 'Max Amount must be greater than or equal to Min Amount.';
        }
        return errs;
      }}
    />
  );
}
